import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { usePlayer, fmtTime } from '@/context/PlayerContext';
import { ShareButton } from '@/components/actions';
import { useState } from 'react';

const FALLBACK = 'https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=200';

export function GlobalPlayer() {
  const { current, playing, progress, duration, volume, toggle, next, prev, seek, setVolume } = usePlayer();
  const [muted, setMuted] = useState(false);
  if (!current) return null;

  const curTime = (progress / 100) * duration;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 glass border-t border-white/10" data-testid="global-player">
      <div className="mx-auto flex max-w-[1600px] items-center gap-4 px-3 py-2.5 sm:px-6">
        {/* Track info */}
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:w-64 sm:flex-none">
          <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg">
            <img src={current.cover_url || FALLBACK} alt="" className="h-full w-full object-cover" />
            {playing && (
              <div className="absolute inset-0 flex items-end justify-center gap-0.5 bg-black/30 p-1">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="eq-bar w-1 rounded-full bg-brand" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate font-display text-sm font-semibold text-white">{current.title}</p>
            <p className="truncate text-xs text-muted-foreground">{current.creator || current.style_text || 'MyMusic IA'}</p>
          </div>
        </div>

        {/* Controls + progress */}
        <div className="flex flex-1 flex-col items-center gap-1">
          <div className="flex items-center gap-3">
            <button onClick={prev} className="hidden text-muted-foreground hover:text-white sm:block" data-testid="player-prev">
              <SkipBack className="h-5 w-5" />
            </button>
            <button onClick={toggle} data-testid="global-audio-play-button"
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-black btn-glow transition-transform hover:scale-105">
              {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 fill-black" />}
            </button>
            <button onClick={next} className="hidden text-muted-foreground hover:text-white sm:block" data-testid="player-next">
              <SkipForward className="h-5 w-5" />
            </button>
          </div>
          <div className="hidden w-full max-w-xl items-center gap-2 sm:flex">
            <span className="w-10 text-right font-mono text-[11px] text-muted-foreground">{fmtTime(curTime)}</span>
            <Slider value={[progress]} max={100} step={0.5} onValueChange={(v) => seek(v[0])}
                    className="flex-1" data-testid="player-seek" />
            <span className="w-10 font-mono text-[11px] text-muted-foreground">{fmtTime(duration)}</span>
          </div>
        </div>

        {/* Right controls */}
        <div className="hidden items-center gap-3 sm:flex sm:w-64 sm:justify-end">
          <ShareButton slug={current.slug} title={current.title} creator={current.creator} />
          <button onClick={() => { setMuted(!muted); setVolume(muted ? 0.9 : 0); }} className="text-muted-foreground hover:text-white" data-testid="player-mute">
            {muted || volume === 0 ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
          <Slider value={[volume * 100]} max={100} onValueChange={(v) => setVolume(v[0] / 100)} className="w-24" />
        </div>
      </div>
      {/* Mobile progress line */}
      <div className="h-0.5 w-full bg-white/10 sm:hidden">
        <div className="h-full bg-brand" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}
